import max from 'lodash/max';
import cloneDeep from 'lodash/cloneDeep';
import calculations from './calculations';
import Layout from './Layout';
import Mapper from './Mapper';
import DependencyConfig from './DependencyConfig';
import TypesObject from './Types';
import Group from './Group';

const _ = {
  cloneDeep,
  max,
};

// const eyes = require('eyes');

const DEFAULT_TYPE = 'String';
const DEFAULT_NAME_KEY = 'fieldName';
const DEFAULT_VALUE_KEY = 'value';

const mapping = {
  name: 'fieldName',
  minLen: 'minlength',
  maxLen: 'maxlength',
  required: 'required',
  type: 'type',
  collection: 'collection',
  pattern: 'pattern',
  maxlengthPerLine: 'maxlengthPerLine',
  maxLines: 'maxLines',
  minListSize: 'minListSize',
  maxListSize: 'maxListSize',
  calculate: 'calculate',
  precision: 'precision',
  skipIndex: 'skipIndex',
  dependencies: 'dependencies',
};

const Types = {};

Types.boolean = TypesObject.Boolean;
Types.color = TypesObject.Color;
Types.creditcard = TypesObject.CreditCard;
Types.currency = TypesObject.Currency;
Types.date = TypesObject.DateFormat;
Types.email = TypesObject.Email;
Types.file = TypesObject.File;
Types.integer = TypesObject.Integer;
Types.json = TypesObject.Json;
Types.number = TypesObject.Numerical;
Types.percent = TypesObject.Percent;
Types.phone = TypesObject.Phone;
Types.string = TypesObject.Text;
Types.zipcode = TypesObject.ZipCode;

/**
 * Validation runner designed to consume a configuration that provides
 * the specifics for constraints or any other form of Validation
 * against a given data set that compliments the configuration.
 */
class Validator {
  /**
   * Initializes a new validator object with optional config and data.
   *
   * @param {Array} config - Configuration definitions.
   * @param {Array} data - The data to be validated.
   */
  constructor(config = [], data = []) {
    this.load(config, data);
  }

  /**
   * Loads a new config and data.  Data is converted into fields with
   * the appropriate type classes.
   *
   * @param {Array} config - Configuration definitions.
   * @param {Array} data - The data to be validated.
   * @return {Validator}
   */
  load(config, data) {
    this.layout = new Layout(config);

    this.config = this.layout.mapFieldKeys(mapping).getLookup();
    this.data = Validator.compileData(_.cloneDeep(data));

    this.fields = [];
    this.fieldLookup = {};
    this.groups = [];
    this.groupLookup = {};

    // eyes.inspect(this.config);
    // eyes.inspect(this.data);

    this.data.forEach(field => {
      const fieldConfig = this.config[field[DEFAULT_NAME_KEY]];
      if (!fieldConfig) {
        // Taking this out since it keeps getting referenced as a problem on steps type pages
        // console.warn(`No field config found for field name ${field[DEFAULT_NAME_KEY]}`);
        return;
      }

      const fieldType = fieldConfig.type && Types[fieldConfig.type.toLowerCase()] ? fieldConfig.type : DEFAULT_TYPE;
      try {
        const TypeClass = Types[fieldType.toLowerCase()];
        const fieldInstance = new TypeClass(fieldConfig, field[DEFAULT_VALUE_KEY]);
        this.fields.push(fieldInstance);
        this.fieldLookup[fieldConfig.fieldName] = fieldInstance;
      } catch (error) {
        console.warn(`Failed to create field of fileType ${fieldType}: ${error.message}`);
        console.warn('Field Data', field);
      }
    }, this);

    // Loop through Groups
    this.layout.forEachGroup((groupName, config) => {
      const groupInstance = new Group(config);
      this.groups.push(groupInstance);
      this.groupLookup[config.key] = groupInstance;

      this.layout.getAllFieldsUnderGroup(groupName).forEach(fieldName => {
        if (this.fieldLookup[fieldName]) {
          this.fieldLookup[fieldName].addGroup(groupInstance);
        }
      });
    });

    this.initDependencyValues();

    return this;
  }

  initDependencyValues() {
    const items = [...this.fields, ...this.groups];

    items.forEach(field => {
      if (!field.config.dependencies) {
        return;
      }

      const configs = Array.isArray(field.config.dependencies)
        ? field.config.dependencies
        : [field.config.dependencies];

      const dependencyIndex = [[]];

      configs.forEach((config, configIndex) => {
        const depConfig = new DependencyConfig(config, this.fieldLookup);

        // Nothing to initialize
        if (!depConfig.isValid()) {
          return;
        }

        // For collections, make the dataSize at least one, so that some action can
        // be performed
        const dataSize = field.config.collection && field.getDataSize() === 0 ? 1 : field.getDataSize();

        for (let index = 0; index < dataSize; index += 1) {
          depConfig.replaceValues(index, field.config.collection);
          if (!dependencyIndex[index]) {
            dependencyIndex[index] = [];
          }
          dependencyIndex[index][configIndex] = depConfig.getNewConfig();
        }
      });

      field.dependencyIndex = dependencyIndex;
    });
  }

  /**
   * Any pre-validation mutations to the data occur here.
   *
   * @param {Array} data - The data to be validated.
   * @return {Array} The mutated data
   *
   * @todo Maybe a data class is needed for separation.
   */
  static compileData(data = []) {
    let newData = [];

    if (Validator.isLegacyData(data)) {
      data.forEach(item => {
        const newItem = {};
        newItem[DEFAULT_NAME_KEY] = item.name;
        newItem[DEFAULT_VALUE_KEY] = item.data;
        newData.push(newItem);
      });
    } else {
      newData = data;
    }

    if (!Array.isArray(newData)) {
      console.warn('Compiled data is not an array');
      console.warn(newData);
    }

    // eyes.inspect(newData);

    return newData;
  }

  /**
   * The primary validation runner for the entire dataset
   * against the config.
   *
   * @return {Object} A report of the results.
   */
  validate() {
    // Need to check deps for groups before validating fields
    this.groups.forEach(group => {
      group.checkDependencies();
    });

    this.fields.forEach(field => {
      // Check dependencies before calculations for deps that 'halt' a calculation
      // TODO only do those...
      field.checkDependencies();

      field.validate();
      try {
        field.sanitize();
      } catch (error) {
        // console.warn(`Sanitation didn't occur for ${field.getId()}: ${error.message}`);
        // console.warn(this.config, field);
      }
    });

    this.calculate();

    // This re-populates the values in the dependencies to have newly calculated values
    // TODO We shouldn't have to do get -- the field.getValue() call should always get/populate the latest value on checkDepencies()
    this.initDependencyValues();

    // Evaluate dependencies after calculations because numbers may have changed
    // this.fields.forEach(field => {
    //   field.checkDependencies();
    // });

    // Any values that changed from calculation needs deps revaluated & re-validated
    if (Array.isArray(this.fieldsChangedFromCalculation) && this.fieldsChangedFromCalculation.length) {
      this.fieldsChangedFromCalculation.forEach(fieldName => {
        const calculatedField = this.fieldLookup[fieldName];
        calculatedField.checkDependencies();

        calculatedField.validate();
        try {
          calculatedField.sanitize();
        } catch (error) {
          // console.warn(`Sanitation didn't occur for ${field.getId()}: ${error.message}`);
          // console.warn(this.config, field);
        }
      });
    }

    return this.report();
  }

  /**
   * Perform auto-calculations
   * Calculation properties: method, params
   *
   * @return {Object} A report of the results.
   */
  calculate() {
    this.alreadyCalculated = {};
    this.fieldsChangedFromCalculation = [];

    const go = field => {
      if (!field.config.calculate) {
        return false;
      }

      // Calculation is set but a method isn't defined
      if (!field.config.calculate.method) {
        console.warn(`Calculate for ${field.getId()} didn't have a method defined.`);
        return false;
      }
      // If the calculation is defined on a field inside of a collection
      // it will be computed relative to its peers
      if (field.config.collection) {
        // Look at each parameter given for the calculation and find the max
        // data size (Skip if it's not a collection)
        const dataSize = _.max(
          field.config.calculate.params.map(fieldName => {
            if (!this.fieldLookup[fieldName] || !this.fieldLookup[fieldName].config.collection) {
              return false;
            }

            return this.fieldLookup[fieldName].getValue().length;
          })
        );

        // Based on the max data size run calculation for each index in the collection
        for (let index = 0; index < dataSize; index += 1) {
          // Skip if it's not meant to be calculated (but entered)
          if (field.hasDependencyAction('entered', index)) {
            continue;
          }

          const values = [];
          field.config.calculate.params.forEach(param => {
            if (this.fieldLookup[param]) {
              values.push(this.fieldLookup[param].getValue(index));
            } else {
              values.push(param);
            }
          });

          try {
            const result = calculations[field.config.calculate.method].apply({}, values);
            field.setValue(result, index).sanitize(true);
          } catch (error) {
            console.warn(`Failed to perform calculation: ${error.message}`);
          }
        }

        return true;
      }

      // Calculate defined on regular field (NOT a collection)

      // Check if calculation is set and that calculation is turned off (deps can turn it off)
      if (field.hasDependencyAction('entered', 0)) {
        return false;
      }

      const values = [];
      field.config.calculate.params.forEach(param => {
        const val = this.fieldLookup[param] ? this.fieldLookup[param].getValue() : param;
        values.push(val);
      });

      try {
        const result = calculations[field.config.calculate.method].apply({}, values);
        field.setValue(result).sanitize(true);
      } catch (error) {
        console.warn(`Failed to perform calculation: ${error.message}`);
      }

      return true;
    };

    // Depth is just a crud check on infinite recursion
    const doCalc = (fieldName, depth = 0) => {
      if (depth > 10) {
        return;
      }
      const field = this.fieldLookup[fieldName];
      const result = go(field);
      const deps = this.layout.getDependencyMap()[field.config.fieldName];

      // This field ran a calculation, check to see if the value changed and note it
      if (result === true) {
        // Only worry about non-tables/collections for now
        if (field.config.collection === false) {
          const newValue = field.value[0];
          if (newValue !== field.originalValue) {
            this.fieldsChangedFromCalculation.push(fieldName);
          }
        }
      }

      // Recursively handle deps
      if (result && Array.isArray(deps)) {
        deps.forEach(dep => {
          if (this.fieldLookup[dep]) {
            doCalc(dep, depth + 1);
          }
        });
      }

      // return go(field); // I don't think this was necessary
      return;
    };

    this.fields.forEach(field => {
      doCalc(field.config.name);
    });
  }

  /**
   * Gives a detailed report of the validation results.
   *
   * @return {Object} A report of the results.
   */
  report() {
    const report = { valid: true, results: {}, failed: {}, steps: [] };

    this.fields.forEach(field => {
      if (!field.isValid()) {
        report.failed[field.getId()] = field.report();
        report.valid = false;
      }
      report.results[field.getId()] = field.report();
    });

    this.groups.forEach(group => {
      report.results[group.getId()] = group.report();
    });

    if (this.layout.hasSteps) {
      const failedFields = Object.keys(report.failed);
      this.layout.steps.forEach((step, index) => {
        const { key } = step;
        const stepFields = this.layout.getAllFieldsUnderGroup(key);
        const stepValid = !failedFields.filter(f => stepFields.includes(f)).length;
        report.steps[index] = stepValid;
      });
    }

    return report;
  }

  /**
   * Checks to see if a config is in a certain format.
   *
   * @param {Array} config - Configuration definitions.
   * @return {boolean}
   */
  static isLegacyConfig(config = []) {
    if (config && config[0] && config[0].name) {
      return true;
    }

    if (config && config[0] && config[0][0] && config[0][0].name) {
      return true;
    }

    return false;
  }

  /**
   * Checks to see if the data is in a certain format.
   *
   * @param {Array} data - The data to be validated.
   * @return {boolean}
   */
  static isLegacyData(data = []) {
    if (data && data[0] && data[0].data !== undefined) {
      return true;
    }

    return false;
  }

  /**
   * Utility to map data structures into different desired formats.
   *
   * @return {Object}
   */
  static mapper() {
    return Mapper;
  }

  /**
   * Utility to validate the structure of a page layout
   *
   * @return {Object}
   */
  static layout() {
    return Layout;
  }

  /**
   * Utility to sanitize a field given a type and value
   *
   * @param {string} type - One of the field types.
   * @param {string} value - Field value.
   * @return {Object}
   */
  static sanitize(type, value, config = {}) {
    const TypeClass = Types[type];
    return new TypeClass(config, value).sanitize(true).getDisplayValue();
  }
}

export default Validator;
